'use client';

import { Box, Container, Flex, HStack, Heading, Text } from '@chakra-ui/react';
import { useRef } from 'react';

import { LinkButton } from '@ui/link-button';

const GREEN = '#1D9E75';
const GREEN_LIGHT = '#E1F5EE';
const GREEN_DARK = '#085041';

const REVIEWS = [
  {
    name: 'Nathalie P.',
    color: '#E37400',
    text: 'Je suis très satisfaite, du professionnalisme de MR Vitry de sa gentillesse, sa patience. Je recommande vivement.',
  },
  {
    name: 'Nadine S.',
    color: '#E37400',
    text: "Je suis tres satisfaite de monsieur vitry qui m'a très bien conseillée et dont le suivi est excellent. Je recommande, pour tous ceux qui ont des doutes sur quelle mutuelle choisir, de le contacter. Merci beaucoup.",
  },
  {
    name: 'Observance',
    color: GREEN,
    text: "Très bon contact avec Monsieur Pol Vitry. Il s'est déplacé deux fois à notre domicile pour nous proposer une solution la plus adaptée. Nous sommes très satisfaits de ces prestations. Je le recommande vivement.",
  },
  {
    name: 'Pascal P.',
    color: '#C0392B',
    text: 'Sérieux réactif professionnel et ponctuel. Il connaît parfaitement sont métier. Je recommande fortement.',
  },
  {
    name: 'Charles R.',
    color: '#0C4850',
    text: "Rencontre très appréciée bonne entretien ét bonne explication. Agreable d'avoir une personne qui réponde a nos demandes merci.",
  },
  {
    name: 'Evelyne',
    color: '#7B3FA0',
    text: "C'est très agréable d'avoir une personne en face à face plutôt qu'en ligne. Pol Vitry est très professionnel, à l'écoute, disponible et propose des solutions adaptées.",
  },
  {
    name: 'Joelle A.',
    color: '#1A73E8',
    text: "Excellent échange avec M Pol Vitry, voulant changer de mutuelle, il nous a bien orienté, de plus notre économie n'est pas négligeable.",
  },
  {
    name: 'Aline C.',
    color: '#E37400',
    text: "Je suis très satisfaite des propositions et suivi de Mr Vitry toujours très disponible et à l'écoute de mes attentes.",
  },
];

const SUMMARY_CHIPS = [
  'Disponibilité & réactivité',
  'Conseils personnalisés',
  'Accompagnement complet',
  'Économies réalisées',
  'Zéro pression commerciale',
];

function ReviewCard({ name, color, text }: { name: string; color: string; text: string }) {
  return (
    <Box
      bg="white"
      borderRadius="14px"
      p="1.6rem 1.5rem"
      minW={{ base: '280px', md: '300px' }}
      maxW="340px"
      flexShrink={0}
      display="flex"
      flexDirection="column"
      gap={3}
      transition="transform 0.2s, box-shadow 0.2s"
      _hover={{ transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.1)' }}
      css={{ scrollSnapAlign: 'start' }}
    >
      <Flex justify="space-between" align="flex-start" gap={2}>
        <HStack gap={3}>
          <Flex
            w="2.4rem"
            h="2.4rem"
            borderRadius="full"
            bg={color}
            color="white"
            align="center"
            justify="center"
            fontWeight="700"
            fontSize="1rem"
            flexShrink={0}
          >
            {name[0]}
          </Flex>
          <Flex direction="column" gap="0.15rem">
            <Text fontWeight="600" fontSize="0.88rem">
              {name}
            </Text>
          </Flex>
        </HStack>
        <Text color="#F59E0B" fontSize="0.85rem" flexShrink={0}>
          ★★★★★
        </Text>
      </Flex>
      <Text fontSize="0.88rem" color="#6B7280" lineHeight={1.7}>
        « {text} »
      </Text>
      <HStack fontSize="0.78rem" color="#9CA3AF" gap={2}>
        <svg width="14" height="14" viewBox="0 0 48 48">
          <path
            fill="#EA4335"
            d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
          />
          <path
            fill="#4285F4"
            d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
          />
          <path
            fill="#FBBC05"
            d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
          />
          <path
            fill="#34A853"
            d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
          />
        </svg>
        <Text>Avis Google</Text>
      </HStack>
    </Box>
  );
}

export default function TestimonialsSection() {
  const trackRef = useRef<HTMLDivElement>(null);

  const scrollByCard = (dir: number) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector<HTMLElement>(':scope > div');
    const width = card ? card.offsetWidth + 19 : 320;
    track.scrollBy({ left: dir * width, behavior: 'smooth' });
  };

  return (
    <Box as="section" py={{ base: 12, lg: 20 }} bg={GREEN_LIGHT} overflow="hidden">
      <Container maxW="1200px" px="5vw">
        <Heading
          as="h2"
          textAlign="center"
          fontSize={{ base: '1.5rem', lg: '2.1rem' }}
          fontWeight="800"
          color={GREEN_DARK}
          maxW="700px"
          mx="auto"
          mb="1.5rem"
          lineHeight={1.25}
          letterSpacing="-0.03em"
        >
          Des centaines de retraités (ou futurs retraités) nous font déjà confiance&nbsp;!
        </Heading>

        <Flex justify="center" mb="1.5rem">
          <HStack
            bg="white"
            borderRadius="full"
            px="1.2rem"
            py="0.55rem"
            boxShadow="0 2px 12px rgba(0,0,0,0.08)"
            gap={3}
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              />
              <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
              />
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              />
            </svg>
            <Text fontWeight="800" fontSize="1.25rem" lineHeight={1}>
              4,9
            </Text>
            <Text color="#F59E0B" fontSize="1rem" letterSpacing="0.04em">
              ★★★★★
            </Text>
            <Text fontSize="0.8rem" color="#6B7280" fontWeight="500" borderLeft="1px solid #E5E7EB" pl={3}>
              Avis Google vérifiés
            </Text>
          </HStack>
        </Flex>

        <HStack justify="center" gap="1.5rem" flexWrap="wrap" mb="2rem">
          {SUMMARY_CHIPS.map((chip) => (
            <HStack
              key={chip}
              bg="rgba(29,158,117,0.12)"
              borderRadius="full"
              px="0.9rem"
              py="0.35rem"
              fontSize="0.82rem"
              fontWeight="600"
              color={GREEN_DARK}
              gap="0.45rem"
            >
              <Text fontWeight="700">✓</Text>
              <Text>{chip}</Text>
            </HStack>
          ))}
        </HStack>
      </Container>

      <Box px={{ base: 4, lg: '5vw' }}>
        <Flex
          ref={trackRef}
          gap="19px"
          overflowX="auto"
          pb={4}
          css={{
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollSnapType: 'x mandatory',
          }}
        >
          {REVIEWS.map((review) => (
            <ReviewCard key={review.name} name={review.name} color={review.color} text={review.text} />
          ))}
        </Flex>

        <HStack justify="center" gap={3} mt={4}>
          <Flex
            as="button"
            w="44px"
            h="44px"
            borderRadius="full"
            bg="white"
            boxShadow="0 2px 10px rgba(0,0,0,0.08)"
            align="center"
            justify="center"
            cursor="pointer"
            onClick={() => scrollByCard(-1)}
            aria-label="Avis précédent"
            _hover={{ bg: '#F9FAFB' }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0C4850"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </Flex>
          <Flex
            as="button"
            w="44px"
            h="44px"
            borderRadius="full"
            bg="white"
            boxShadow="0 2px 10px rgba(0,0,0,0.08)"
            align="center"
            justify="center"
            cursor="pointer"
            onClick={() => scrollByCard(1)}
            aria-label="Avis suivant"
            _hover={{ bg: '#F9FAFB' }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#0C4850"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Flex>
        </HStack>
      </Box>

      <HStack justify="center" gap="1rem" mt="2.5rem" flexWrap="wrap">
        <LinkButton
          href="/devis"
          css={{
            background: GREEN,
            color: '#fff',
            border: `2px solid ${GREEN}`,
            borderRadius: '10px',
            padding: '0.88rem 1.75rem',
            fontSize: '1rem',
            fontWeight: '700',
            '&:hover': { background: GREEN_DARK, borderColor: GREEN_DARK },
          }}
        >
          Créer son devis (2&nbsp;min)&nbsp;→
        </LinkButton>
        <LinkButton
          href="/rdv"
          css={{
            background: 'transparent',
            color: GREEN,
            border: `2px solid ${GREEN}`,
            borderRadius: '10px',
            padding: '0.86rem 1.55rem',
            fontSize: '0.94rem',
            fontWeight: '600',
            '&:hover': { background: GREEN_LIGHT },
          }}
        >
          Prendre un rendez-vous à domicile
        </LinkButton>
      </HStack>
    </Box>
  );
}
